import React, { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import { sbFetch } from "../utils/supabaseClient";
import { sendNotification } from "../utils/notifications";
import { useToast } from "./Toast";
import PreviewModal from "./PreviewModal";
import EventEditModal from "./EventEditModal";
import PullToRefresh from "./PullToRefresh";
import { SVG_ICONS } from "./TablerIconPicker";
import "../styles/ListTab.css";

const EventListTab = forwardRef(({ onHeaderInfoChange }, ref) => {
  const { success, error } = useToast();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewContent, setPreviewContent] = useState(null);
  const [previewTitle, setPreviewTitle] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedTimeframe, setSelectedTimeframe] = useState("all");
  const [selectedExpire, setSelectedExpire] = useState(null);

  useEffect(() => {
    loadEvents();
  }, []);

  useImperativeHandle(ref, () => ({
    loadEvents
  }));

  useEffect(() => {
    onHeaderInfoChange?.({ title: "Events", count: events.length });
  }, [events, onHeaderInfoChange]);

  const loadEvents = async () => {
    try {
      setLoading(true);
      const data = await sbFetch("GET", "events?order=date_time.desc");
      setEvents(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error loading events:", err);
      error("Failed to load events");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
  };

  const handleSaveEdit = async (updatedEvent) => {
    const oldEvent = events.find(e => e.id === editingId);
    const wasPublished = oldEvent?.draft && !updatedEvent.draft;

    setEvents(events.map(e => e.id === editingId ? { ...e, ...updatedEvent } : e));

    if (wasPublished) {
      await sendNotification(
        `📅 New Event • ${updatedEvent.title}`,
        updatedEvent.title,
        "events",
        editingId
      );
    }

    setEditingId(null);
    success("Event updated");
  };

  const handleCancel = () => {
    setEditingId(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this event?")) return;

    try {
      await sbFetch("DELETE", `events?id=eq.${id}`);
      setEvents(events.filter((e) => e.id !== id));
      success("Event deleted");
    } catch (err) {
      console.error("Error deleting event:", err);
      error("Failed to delete event");
    }
  };

  const handlePreview = (item) => {
    const eventDate = new Date(item.date_time);
    const content = (
      <div style={{ width: "100%", maxWidth: "100%" }}>
        <h1 style={{ color: "#5B9EF0", marginTop: 0 }}>{item.title}</h1>

        <div style={{ background: "rgba(59, 130, 246, 0.05)", border: "1px solid rgba(59, 130, 246, 0.2)", borderRadius: "8px", padding: "16px", marginBottom: "20px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
          <div style={{ color: "#7A9CC4", fontSize: "14px", display: "flex", alignItems: "center", gap: "6px" }}>
            {SVG_ICONS["calendar"]({ size: 16, color: "#7A9CC4" })}
            {eventDate.toLocaleDateString()} at {eventDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </div>

          {item.location && (
            <div style={{ color: "#7A9CC4", fontSize: "14px", display: "flex", alignItems: "center", gap: "6px" }}>
              {SVG_ICONS["home"]({ size: 16, color: "#7A9CC4" })}
              {item.location}
            </div>
          )}

          {item.speaker_name && (
            <div style={{ color: "#7A9CC4", fontSize: "14px", display: "flex", alignItems: "center", gap: "6px" }}>
              {SVG_ICONS["graduation-cap"]({ size: 16, color: "#7A9CC4" })}
              {item.speaker_name}
              {item.speaker_title && ` • ${item.speaker_title}`}
            </div>
          )}

          <div style={{ color: "#7A9CC4", fontSize: "14px", display: "flex", alignItems: "center", gap: "6px" }}>
            {SVG_ICONS["bulb"]({ size: 16, color: "#7A9CC4" })}
            {item.event_type.charAt(0).toUpperCase() + item.event_type.slice(1)}
          </div>

          {item.capacity && (
            <div style={{ color: "#7A9CC4", fontSize: "14px" }}>
              Capacity: {item.capacity}
            </div>
          )}
        </div>

        {item.image_url && (
          <img
            src={item.image_url}
            alt={item.title}
            style={{
              maxWidth: "100%",
              height: "auto",
              borderRadius: "8px",
              marginBottom: "20px",
            }}
          />
        )}

        {item.description && (
          <div style={{ marginBottom: "20px", color: "#C9D1D9", lineHeight: "1.6" }}>
            {item.description}
          </div>
        )}

        {item.registration_url && (
          <a
            href={item.registration_url}
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: "none", display: "inline-block", marginTop: "20px" }}
          >
            <button
              style={{
                padding: "10px 20px",
                background: "rgba(59, 130, 246, 0.15)",
                color: "#5B9EF0",
                border: "1px solid rgba(59, 130, 246, 0.3)",
                borderRadius: "8px",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: "600",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => {
                e.target.style.background = "rgba(59, 130, 246, 0.25)";
                e.target.style.borderColor = "rgba(59, 130, 246, 0.5)";
              }}
              onMouseLeave={(e) => {
                e.target.style.background = "rgba(59, 130, 246, 0.15)";
                e.target.style.borderColor = "rgba(59, 130, 246, 0.3)";
              }}
            >
              📝 Register for Event
            </button>
          </a>
        )}
      </div>
    );

    setPreviewTitle(`Event: ${item.title}`);
    setPreviewContent(content);
    setPreviewOpen(true);
  };

  const handleToggleDraft = async (item) => {
    try {
      const newDraftStatus = !item.draft;

      await sbFetch("PATCH", `events?id=eq.${item.id}`, {
        draft: newDraftStatus,
      });

      const updatedEvents = events.map(e =>
        e.id === item.id ? { ...e, draft: newDraftStatus } : e
      );
      setEvents(updatedEvents);

      if (!newDraftStatus && item.draft) {
        await sendNotification(
          `📅 New Event • ${item.title}`,
          item.title,
          "events",
          item.id
        );
      }
      success(newDraftStatus ? "Moved to draft" : "Published");
    } catch (err) {
      console.error("Error toggling draft status:", err);
      error("Failed to update event status");
    }
  };

  const isEventUpcoming = (dateTime) => {
    return new Date(dateTime) > new Date();
  };

  if (loading) return <div className="loading">Loading events...</div>;

  if (events.length === 0)
    return <div className="empty-state">No events found. Create one first!</div>;

  const filteredEvents = events.filter((item) => {
    // Filter by search query
    const matchesSearch =
      item.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.speaker_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.location?.toLowerCase().includes(searchQuery.toLowerCase());

    // Filter by status
    const matchesStatus = selectedStatus === null || (
      (selectedStatus === "published" && !item.draft) ||
      (selectedStatus === "draft" && item.draft)
    );

    // Filter by timeframe
    const matchesTimeframe = selectedTimeframe === "all" || (
      (selectedTimeframe === "upcoming" && isEventUpcoming(item.date_time)) ||
      (selectedTimeframe === "past" && !isEventUpcoming(item.date_time))
    );

    // Filter by expiration
    const isExpired = !isEventUpcoming(item.date_time);
    const matchesExpire = selectedExpire === null || (
      (selectedExpire === "active" && !isExpired) ||
      (selectedExpire === "expired" && isExpired)
    );

    return matchesSearch && matchesStatus && matchesTimeframe && matchesExpire;
  });

  const editingEvent = events.find((e) => e.id === editingId);

  return (
    <div className="list-container">
      <PreviewModal
        isOpen={previewOpen}
        onClose={() => setPreviewOpen(false)}
        content={previewContent}
        title={previewTitle}
      />

      {editingEvent && (
        <EventEditModal
          event={editingEvent}
          onSave={handleSaveEdit}
          onCancel={handleCancel}
        />
      )}

      <input
        type="text"
        placeholder="Search events by title, speaker, or location..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        style={{
          width: "100%",
          padding: "10px 12px",
          marginBottom: "12px",
          background: "#050e1a",
          border: "1px solid rgba(59, 130, 246, 0.2)",
          borderRadius: "6px",
          color: "#fff",
          fontSize: "14px",
          boxSizing: "border-box",
        }}
      />

      {/* Status Filter Pills */}
      <div style={{ display: "flex", gap: "8px", marginBottom: "12px", flexWrap: "wrap" }}>
        <button
          onClick={() => setSelectedStatus(null)}
          style={{
            padding: "6px 14px",
            background: selectedStatus === null ? "rgba(59, 130, 246, 0.2)" : "rgba(59, 130, 246, 0.05)",
            color: selectedStatus === null ? "#5B9EF0" : "#7A9CC4",
            border: selectedStatus === null ? "1px solid rgba(59, 130, 246, 0.4)" : "1px solid rgba(59, 130, 246, 0.2)",
            borderRadius: "20px",
            cursor: "pointer",
            fontSize: "13px",
            fontWeight: "500",
            transition: "all 0.2s",
          }}
        >
          All
        </button>
        {["published", "draft"].map((status) => {
          const count = events.filter(item => (
            status === "published" ? !item.draft : item.draft
          )).length;
          const label = status.charAt(0).toUpperCase() + status.slice(1);
          return (
            <button
              key={status}
              onClick={() => setSelectedStatus(status)}
              style={{
                padding: "6px 14px",
                background: selectedStatus === status ? "rgba(59, 130, 246, 0.2)" : "rgba(59, 130, 246, 0.05)",
                color: selectedStatus === status ? "#5B9EF0" : "#7A9CC4",
                border: selectedStatus === status ? "1px solid rgba(59, 130, 246, 0.4)" : "1px solid rgba(59, 130, 246, 0.2)",
                borderRadius: "20px",
                cursor: "pointer",
                fontSize: "13px",
                fontWeight: "500",
                transition: "all 0.2s",
              }}
            >
              {label} ({count})
            </button>
          );
        })}
      </div>

      {/* Timeframe Filter Pills */}
      <div style={{ display: "flex", gap: "8px", marginBottom: "20px", flexWrap: "wrap" }}>
        {["all", "upcoming", "past"].map((timeframe) => {
          const label = timeframe === "all" ? "All Events" : timeframe.charAt(0).toUpperCase() + timeframe.slice(1);
          return (
            <button
              key={timeframe}
              onClick={() => setSelectedTimeframe(timeframe)}
              style={{
                padding: "6px 14px",
                background: selectedTimeframe === timeframe ? "rgba(59, 130, 246, 0.2)" : "rgba(59, 130, 246, 0.05)",
                color: selectedTimeframe === timeframe ? "#5B9EF0" : "#7A9CC4",
                border: selectedTimeframe === timeframe ? "1px solid rgba(59, 130, 246, 0.4)" : "1px solid rgba(59, 130, 246, 0.2)",
                borderRadius: "20px",
                cursor: "pointer",
                fontSize: "13px",
                fontWeight: "500",
                transition: "all 0.2s",
              }}
            >
              {label}
            </button>
          );
        })}
      </div>

      {/* Expiration Filter Pills */}
      <div style={{ display: "flex", gap: "8px", marginBottom: "20px", flexWrap: "wrap" }}>
        {[
          { value: null, label: "All Events" },
          { value: "active", label: "Active" },
          { value: "expired", label: "Expired" },
        ].map(({ value, label }) => (
          <button
            key={value || "all"}
            onClick={() => setSelectedExpire(value)}
            style={{
              padding: "6px 14px",
              background: selectedExpire === value ? "rgba(59, 130, 246, 0.2)" : "rgba(59, 130, 246, 0.05)",
              color: selectedExpire === value ? "#5B9EF0" : "#7A9CC4",
              border: selectedExpire === value ? "1px solid rgba(59, 130, 246, 0.4)" : "1px solid rgba(59, 130, 246, 0.2)",
              borderRadius: "20px",
              cursor: "pointer",
              fontSize: "13px",
              fontWeight: "500",
              transition: "all 0.2s",
            }}
          >
            {label}
          </button>
        ))}
      </div>

      <PullToRefresh onRefresh={loadEvents}>
        {filteredEvents.length === 0 && searchQuery && (
          <div className="empty-state">No events match your search</div>
        )}
        {filteredEvents.length > 0 && (
          <div className="items-grid">
            {filteredEvents.map((item) => {
              const eventDate = new Date(item.date_time);
              const isUpcoming = isEventUpcoming(item.date_time);

              return (
                <div key={item.id} className="item-card">
                  {item.image_url && (
                    <img src={item.image_url} alt={item.title} style={{ width: "100%", height: "150px", objectFit: "cover", borderRadius: "8px", marginBottom: "12px" }} />
                  )}
                  <div className="item-content">
                    <h4>{item.title}</h4>
                    <p className="item-meta" style={{ display: "flex", alignItems: "center", gap: "6px", margin: "4px 0" }}>
                      {SVG_ICONS["calendar"]({ size: 14, color: "#7A9CC4" })}
                      {eventDate.toLocaleDateString()}
                    </p>
                    <p className="item-meta" style={{ display: "flex", alignItems: "center", gap: "6px", margin: "4px 0" }}>
                      {SVG_ICONS["hourglass"]({ size: 14, color: "#7A9CC4" })}
                      {eventDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                    {item.location && (
                      <p className="item-meta" style={{ display: "flex", alignItems: "center", gap: "6px", margin: "4px 0", fontSize: "12px" }}>
                        {SVG_ICONS["home"]({ size: 14, color: "#7A9CC4" })}
                        {item.location}
                      </p>
                    )}
                    {item.speaker_name && (
                      <p className="item-meta" style={{ display: "flex", alignItems: "center", gap: "6px", margin: "4px 0", fontSize: "12px" }}>
                        {SVG_ICONS["graduation-cap"]({ size: 14, color: "#7A9CC4" })}
                        {item.speaker_name}
                      </p>
                    )}
                    <div style={{ display: "flex", gap: "8px", marginTop: "8px", flexWrap: "wrap" }}>
                      {item.draft ? (
                        <div style={{ display: "flex", alignItems: "center", gap: "4px", padding: "4px 12px", background: "rgba(245, 158, 11, 0.2)", color: "#F59E0B", borderRadius: "6px", fontSize: "12px", fontWeight: "600" }}>
                          {SVG_ICONS["alert-circle"]({ size: 14, color: "#F59E0B" })}
                          DRAFT
                        </div>
                      ) : (
                        <div style={{ display: "flex", alignItems: "center", gap: "4px", padding: "4px 12px", background: "#22C55E20", color: "#22C55E", borderRadius: "6px", fontSize: "12px", fontWeight: "600" }}>
                          {SVG_ICONS["check-circle"]({ size: 14, color: "#22C55E" })}
                          PUBLISHED
                        </div>
                      )}
                      <div style={{ display: "flex", alignItems: "center", gap: "4px", padding: "4px 12px", background: isUpcoming ? "rgba(59, 130, 246, 0.1)" : "rgba(107, 114, 128, 0.1)", color: isUpcoming ? "#5B9EF0" : "#6B7280", borderRadius: "6px", fontSize: "12px", fontWeight: "600" }}>
                        {SVG_ICONS[isUpcoming ? "check-circle" : "circle-x"]({ size: 14, color: isUpcoming ? "#5B9EF0" : "#6B7280" })}
                        {isUpcoming ? "UPCOMING" : "PAST"}
                      </div>
                    </div>
                  </div>
                  <div className="item-actions">
                    <button
                      onClick={() => handlePreview(item)}
                      title="Preview"
                      style={{
                        padding: "6px 12px",
                        background: "rgba(59, 130, 246, 0.1)",
                        color: "#5B9EF0",
                        border: "1px solid rgba(59, 130, 246, 0.2)",
                        borderRadius: "6px",
                        cursor: "pointer",
                        fontSize: "14px",
                        transition: "all 0.2s",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = "rgba(59, 130, 246, 0.2)";
                        e.target.style.borderColor = "rgba(59, 130, 246, 0.4)";
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = "rgba(59, 130, 246, 0.1)";
                        e.target.style.borderColor = "rgba(59, 130, 246, 0.2)";
                      }}
                    >
                      {SVG_ICONS["eye"]({ size: 18, color: "#5B9EF0" })}
                    </button>
                    <button
                      onClick={() => handleToggleDraft(item)}
                      title={item.draft ? "Publish event" : "Revert to draft"}
                      style={{
                        padding: "6px 12px",
                        background: item.draft ? "rgba(34, 197, 94, 0.1)" : "rgba(245, 158, 11, 0.1)",
                        color: item.draft ? "#22C55E" : "#F59E0B",
                        border: item.draft ? "1px solid rgba(34, 197, 94, 0.2)" : "1px solid rgba(245, 158, 11, 0.2)",
                        borderRadius: "6px",
                        cursor: "pointer",
                        fontSize: "14px",
                        transition: "all 0.2s",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "4px",
                      }}
                      onMouseEnter={(e) => {
                        if (item.draft) {
                          e.target.style.background = "rgba(34, 197, 94, 0.2)";
                          e.target.style.borderColor = "rgba(34, 197, 94, 0.4)";
                        } else {
                          e.target.style.background = "rgba(245, 158, 11, 0.2)";
                          e.target.style.borderColor = "rgba(245, 158, 11, 0.4)";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (item.draft) {
                          e.target.style.background = "rgba(34, 197, 94, 0.1)";
                          e.target.style.borderColor = "rgba(34, 197, 94, 0.2)";
                        } else {
                          e.target.style.background = "rgba(245, 158, 11, 0.1)";
                          e.target.style.borderColor = "rgba(245, 158, 11, 0.2)";
                        }
                      }}
                    >
                      {item.draft
                        ? SVG_ICONS["check"]({ size: 18, color: "#22C55E" })
                        : SVG_ICONS["circle-x"]({ size: 18, color: "#F59E0B" })}
                      <span style={{ fontSize: "12px" }}>
                        {item.draft ? "Publish" : "Draft"}
                      </span>
                    </button>
                    <button
                      onClick={() => handleEdit(item)}
                      title="Edit"
                      style={{
                        padding: "6px 12px",
                        background: "rgba(59, 130, 246, 0.1)",
                        color: "#5B9EF0",
                        border: "1px solid rgba(59, 130, 246, 0.2)",
                        borderRadius: "6px",
                        cursor: "pointer",
                        fontSize: "14px",
                        transition: "all 0.2s",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = "rgba(59, 130, 246, 0.2)";
                        e.target.style.borderColor = "rgba(59, 130, 246, 0.4)";
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = "rgba(59, 130, 246, 0.1)";
                        e.target.style.borderColor = "rgba(59, 130, 246, 0.2)";
                      }}
                    >
                      {SVG_ICONS["pencil"]({ size: 18, color: "#5B9EF0" })}
                    </button>
                    <button
                      onClick={() => handleDelete(item.id)}
                      style={{
                        padding: "6px 12px",
                        background: "rgba(59, 130, 246, 0.1)",
                        color: "#5B9EF0",
                        border: "1px solid rgba(59, 130, 246, 0.2)",
                        borderRadius: "6px",
                        cursor: "pointer",
                        fontSize: "14px",
                        transition: "all 0.2s",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = "rgba(59, 130, 246, 0.2)";
                        e.target.style.borderColor = "rgba(59, 130, 246, 0.4)";
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = "rgba(59, 130, 246, 0.1)";
                        e.target.style.borderColor = "rgba(59, 130, 246, 0.2)";
                      }}
                    >
                      {SVG_ICONS["trash"]({ size: 18, color: "#5B9EF0" })}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </PullToRefresh>
    </div>
  );
});

EventListTab.displayName = 'EventListTab';

export default EventListTab;
